// NPM packages
import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

// All other imports
import Link from 'components/core/Link';
import FullWidthTypography from 'components/core/FullWidthTypography';
import Project from 'types/Project';
import JoinButton from 'components/project/JoinButton';
import ProjectContact from 'components/project/ProjectContact';

const Root = styled.div<{ flipped: boolean | undefined; numImages: number }>(
  ({ theme, flipped, numImages }) => {
    return `
      min-width: ${theme.maxToolWidth};
      height: auto;
      display: grid;
      justify-content: center;
      align-items: center;
      grid-template-columns: ${
        numImages > 0
          ? flipped
            ? '51rem 26.49rem'
            : '26.49rem 51rem'
          : '45rem'
      };
      margin: 2rem 0;
      gap: 2rem;
      padding: 0 ${theme.typography.pxToRem(87)};

      @media (max-width: 1200px) {
        min-width: 0;
        width: 100%;
        height: auto;
        grid-template-columns: 1fr;
        margin: 1.5rem 0;
        padding: 0 ${theme.typography.pxToRem(43.5)};
        gap: 1.5rem;
      }

      @media (max-width: 600px) {
        margin: 1rem 0;
        padding: 0 1rem;
        gap: 1rem;
      }
    `;
  }
);

const Header = styled(FullWidthTypography)(({ theme }) => ({
  marginTop: '0',
  marginBottom: '0.5rem',
  display: 'flex',
  gap: '1rem',
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(28),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

const Subtitle = styled.div(({ theme }) => {
  return {
    fontSize: '1.125rem',
    fontWeight: 'bold',
    paddingBottom: '0.325rem',
    margin: '0px 0px 0.25rem',
  };
});

const Description = styled.div(({ theme }) => ({
  marginBottom: '0.5rem',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(16),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

const Thumbnail = styled.div<{ flipped: boolean }>(({ theme, flipped }) => {
  return {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    display: 'flex',
    justifyContent: flipped ? 'right' : 'left',
    alignItems: 'start',
    flexShrink: 0,
  };
});

const ContentWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  justifySelf: 'auto',
});

interface ProjectListingProps {
  project: Project;
  flipped?: boolean;
  className?: string;
}

function ProjectListing(props: ProjectListingProps): React.ReactElement | null {
  const { project, flipped, className } = props;
  const title = project.title;
  const subtitle = project.subtitle;
  const description = project.description;
  const contact = project.contact;
  const image = project.links.logo;
  const linkedUrl = `${project.slug}`;
  const linksToExternalSite = linkedUrl.includes('http');

  const thumbnail = (
    <Thumbnail flipped={flipped || false}>
      <img src={image} alt={''} width={336} />
    </Thumbnail>
  );

  return (
    <Root flipped={flipped} numImages={1} className={className}>
      {flipped ? null : thumbnail}
      <ContentWrapper>
        <Header variant="h2">
          <Link to={linkedUrl} openNewTab={linksToExternalSite}>
            {title}
          </Link>
          <JoinButton project={project} includeEdit={false} />
        </Header>
        <Subtitle>{subtitle}</Subtitle>
        <Description>{description}</Description>
        <ProjectContact contact={contact} />
      </ContentWrapper>
      {flipped ? thumbnail : null}
    </Root>
  );
}

export default observer(ProjectListing);
